import { css } from '@emotion/css';
import { createSignal, Show } from 'solid-js'
import { Match, Switch } from 'solid-js'
import { Content } from 'Shared/backend/widgets/Content';
import { initContent } from 'Shared/backend/widgets/LayoutSelector';
import { useStore } from 'Shared/StoreProvider';
import { OverlayComponent } from 'Shared/backend/widgets/OverlayComponent';
import type {VisualWithInfoLayout,StylesLayout,EmbedLayout,ImageLayout,Layout,OverlayLayout,PopupLayout,PostersLayout,SlidesLayout, VerticalLayout, TextLayout, ButtonsLayout, GridLayout, LayoutTypes, LinkLayout} from 'Shared/model/Components';
import { Slides } from 'Shared/backend/widgets/Slides';
import { ImageBlock } from 'Shared/backend/widgets/ImageBlock';
import { Embed } from 'Shared/backend/widgets/Embed';
import { Styles } from 'Shared/backend/widgets/Styles';
import { Popup } from 'Shared/backend/widgets/Popup';
import { VisualWithInfo } from 'Shared/backend/widgets/VisualWithInfo';
import { Vertical } from 'Shared/backend/widgets/Vertical';
import { TextWidget } from 'Shared/backend/widgets/TextWidget';
import { Grid } from 'Shared/backend/widgets/Grid';
import { Buttons } from 'Shared/backend/widgets/Buttons';
import { LinkBlock } from 'Shared/backend/widgets/LinkBlock';
import { reconcile } from 'solid-js/store';
import { subdoc } from 'Common/ViewUtils';
import { theme } from 'Shared/ThemeProvider';
import { useLayout } from 'Shared/backend/LayoutProvider';


/*
	LAYOUT THOUGHTS

	Generally children will have a width of 100%, with the parents determining how much width it to be used.
	The Overlay foreground needs a width set on it, but note that the Overlay as a whole still generally uses 100% width.

	Which components need heights? And which height options to include?
	As a start, imagine adding the components directly to a section and ask:
	will the height always or sometimes come up from the children?
	If the height is always known we won't want a height setting, otherwise we will.

	What about "gaps"? Do we want to be able to set heights on these?

	Some "gaps" sit at the top of the tree. These are:
	  - sections
	  - overlay foregrounds
	  - popup content 
	  - visual with info visuals
	Now while it would be possible to allow sections to set heights, we are not currently doing so.
	This implies that all components that can be added to sections (which are all of them) either have a height already
	own height and/or can set their own height. 
	This implies overlay foregrounds, popup content, and visual with info visuals don't need height settings.

	What about other types of gaps? Providing row heights for sliders and grids allow for 
	consistency and convenience.
*/

export function Component(P:Layout)
{
	const lo = useLayout();

	return (<>
		<Switch>
			<Match when={P.layout == 'styles'}>
				<Styles {...P as StylesLayout} /> 
			</Match>
			<Match when={P.layout == 'link'}>
				<LinkBlock {...P as LinkLayout} /> 
			</Match>
			<Match when={P.layout == 'vertical'}>
				<Vertical {...P as VerticalLayout} />
			</Match>
			<Match when={P.layout == 'grid'}>
				<Grid {...P as GridLayout} />
			</Match>
			<Match when={P.layout == 'text'}>
				<TextWidget {...P as TextLayout} />
			</Match>
			<Match when={P.layout == 'slides'}>
				<Slides {...P as SlidesLayout} />
			</Match>
			<Match when={P.layout == 'image'}>
				<ImageBlock {...P as ImageLayout} /> 
			</Match>
			<Match when={P.layout == 'overlay'}>
				<OverlayComponent {...P as OverlayLayout} />
			</Match>
			<Match when={P.layout == 'visualWithInfo'}>
				<VisualWithInfo {...P as VisualWithInfoLayout} /> 
			</Match>
			<Match when={P.layout == 'posters'}>
				<Content {...P as PostersLayout} />
			</Match>
			<Match when={P.layout == 'buttons'}>
				<Buttons {...P as ButtonsLayout} />
			</Match>
			<Match when={P.layout == 'popup'}>
				<Popup {...P as PopupLayout} />
			</Match>
			<Match when={P.layout == 'embed'}>
				<Embed {...P as EmbedLayout} />
			</Match>
		</Switch>
	</>);
}

export function AddWrap(P)
{
	const wrapStyle = css({
		width: '12em'
	});

	const hierarchyStyle = css(theme().a,{
		alignSelf: 'start',
		color: theme().palette.label,

		'&:visited': {
			color: theme().palette.label
		}
	});

	const sw = useStore();
	const [show,setShow] = createSignal(false);

//TODO dont allow a slider in a slider
//TODO maybe allow grid to be wrapped

	const haveOne = items => items?.length == 1;

	return (
		<div class={wrapStyle}>
			<Switch>
				<Match when={!show()}>
					<a class={hierarchyStyle} href='' onClick={() => setShow(true)}><i class='fas fa-caret-right'/> Edit hierarchy</a>
				</Match>
				<Match when={show()}>
					<a class={hierarchyStyle} href='' onClick={() => setShow(false)}><i class='fas fa-caret-down'/> Edit hierarchy</a>
					<div>
						<a class={css(theme().a)} href='' onClick={e => addWrap(e,sw,setShow,'styles')}>Add a styles component</a> <br/>
						<a class={css(theme().a)} href='' onClick={e => addWrap(e,sw,setShow,'vertical')}>Add a vertical layout</a> <br/>
						<a class={css(theme().a)} href='' onClick={e => addWrap(e,sw,setShow,'slides')}>Add a slider</a><br/>
						<a class={css(theme().a)} href='' onClick={e => addWrap(e,sw,setShow,'link')}>Add a link block</a> <br/>

						<Show when={P.layout == 'styles'}>
							<a class={css(theme().a)} href='' onClick={e => deleteStyles(e,sw,setShow)}>Delete styles</a><br/>
						</Show>
						<Show when={P.layout == 'slides' && haveOne(P.slides)}>
							<a class={css(theme().a)} href='' onClick={e => deleteSlider(e,sw,setShow)}>Delete slider</a><br/>
						</Show>
						<Show when={P.layout == 'vertical' && haveOne(P.items)}>
							<a class={css(theme().a)} href='' onClick={e => deleteVertical(e,sw,setShow)}>Delete vertical layout</a><br/>
						</Show>
						<Show when={P.layout == 'grid' && haveOne(P.items)}>
							<a class={css(theme().a)} href='' onClick={e => deleteGrid(e,sw,setShow)}>Delete grid</a><br/>
						</Show>
						<Show when={P.layout == 'link'}>
							<a class={css(theme().a)} href='' onClick={e => deleteLink(e,sw,setShow)}>Delete link block</a><br/>
						</Show>
					</div>
				</Match>
			</Switch>
		</div>
	);
}


//TODO open up/focus new item and close all children for clarity
//NOTE that the new component is automatically openned more or less by accident at the moment and closing of children is not reliable
//XXX cf using Provider to determine what is showing


async function addWrap(e:Event,sw,setShow,componentType:LayoutTypes)
{
	e.preventDefault();
	setShow(false);

	const isTop = sw.location.length == 2;
	let wrapper = initContent(undefined,componentType,isTop);

	/* Stringifying here to prevent the proxies from getting into an endless loop when calling setStore */
	if (componentType == 'styles')
		wrapper = {...wrapper, content: JSON.parse(JSON.stringify((subdoc(sw.store,sw.location)))) };
	else if (componentType == 'vertical') 
		wrapper = {...wrapper, items: [ JSON.parse(JSON.stringify((subdoc(sw.store,sw.location)))) ]};
	else if (componentType == 'slides') 
		wrapper = {...wrapper, slides: [ JSON.parse(JSON.stringify((subdoc(sw.store,sw.location)))) ]};
//FIXME? doesnt make sense to nest a link directly in another. More distant nesting is aso weird.
	else if (componentType == 'link')
		wrapper = {...wrapper,openInSameWindow:true,content: JSON.parse(JSON.stringify((subdoc(sw.store,sw.location)))) };

	await sw.server.replace.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:wrapper});
	sw.setStore(...sw.location,wrapper);
}

async function deleteStyles(e:Event,sw,setShow)
{
	e.preventDefault();

	if (!confirm('Delete styles? It will be replaced by its content.'))
		return;
	setShow(false);

	const content = {
		...JSON.parse(JSON.stringify(subdoc(sw.store,sw.location).content)),
		content: undefined
	};

	await sw.server.patch.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:content});
	sw.setStore(...sw.location,reconcile(content));
}

async function deleteSlider(e:Event,sw,setShow)
{
	e.preventDefault();

	if (!confirm('Delete this slider? It will be replaced by its slide.'))
		return;
	setShow(false);

	const content = {
		...JSON.parse(JSON.stringify(subdoc(sw.store,sw.location).slides[0])),
		slides: undefined
	};

	await sw.server.patch.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:content});
	sw.setStore(...sw.location,reconcile(content));
}

async function deleteVertical(e:Event,sw,setShow)
{
	e.preventDefault();

	if (!confirm('Delete this vertical layout? It will be replaced by its item.'))
		return;
	setShow(false);

	const content = {
		...JSON.parse(JSON.stringify(subdoc(sw.store,sw.location).items[0])),
		items: undefined
	};

	await sw.server.patch.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:content});
	sw.setStore(...sw.location,reconcile(content));
}

async function deleteGrid(e:Event,sw,setShow)
{
	e.preventDefault();

	if (!confirm('Delete this grid? It will be replaced by its cell.'))
		return;
	setShow(false);

	const content = {
		...JSON.parse(JSON.stringify(subdoc(sw.store,sw.location).items[0])),
		items: undefined
	};

	await sw.server.patch.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:content});
	sw.setStore(...sw.location,reconcile(content));
}


async function deleteLink(e:Event,sw,setShow)
{
	e.preventDefault();

	if (!confirm('Delete this link block? It will be replaced by its content.'))
		return;
	setShow(false);

	const content = {...JSON.parse(JSON.stringify(subdoc(sw.store,sw.location).content)),
		content: undefined
	};

	await sw.server.replace.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:content});
	sw.setStore(...sw.location,reconcile(content));
}

